import React, { useState } from 'react';
import adminService from '../services/adminService';

const slugify = (value) => {
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .substring(0, 40);
};

const UnitsManagement = () => {
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [errorUnits, setErrorUnits] = useState('');
  
  const [form, setForm] = useState({ name: '', key: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  // Edição inline
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', key: '' });
  const [editSaving, setEditSaving] = useState(false);

  const fetchUnits = async () => {
    try {
      setLoadingUnits(true);
      setErrorUnits('');
      const data = await adminService.getUnits();
      setUnits(data);
    } catch (err) {
      setErrorUnits(err.message || 'Erro ao carregar unidades');
    } finally {
      setLoadingUnits(false);
    }
  };

  React.useEffect(() => {
    fetchUnits();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'name' && !form.key) {
      setForm((prev) => ({ ...prev, key: slugify(value) }));
    }
  };

  const validate = () => {
    if (!form.name.trim()) {
      setError('Nome da unidade é obrigatório');
      return false;
    }
    if (!form.key.trim()) {
      setError('Chave da unidade é obrigatória');
      return false;
    }
    const keyRegex = /^[a-z0-9-]{2,40}$/;
    if (!keyRegex.test(form.key)) {
      setError('Chave deve conter apenas letras minúsculas, números e hífen (2-40 caracteres).');
      return false;
    }
    return true;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!validate()) return;
    try {
      setSaving(true);
      await adminService.createUnit({ name: form.name.trim(), key: form.key.trim() });
      setMessage('Unidade criada com sucesso!');
      setForm({ name: '', key: '' });
      await fetchUnits();
    } catch (err) {
      setError(err.message || 'Falha ao criar unidade');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (unitId, currentStatus) => {
    if (!unitId) return;
    setMessage('');
    setError('');
    try {
      // Usa o ID da unidade como chave de loading se quisesse loading individual, 
      // mas aqui usando saving global simplificado ou poderia criar um map de loading
      await adminService.updateUnit(unitId, { isActive: !currentStatus });
      setMessage(`Unidade ${!currentStatus ? 'ativada' : 'desativada'} com sucesso!`);
      await fetchUnits();
    } catch (err) {
      setError(err.message || 'Falha ao atualizar status da unidade');
    }
  };

  const handleDelete = async (unitId) => {
    if (!unitId || !window.confirm('Tem certeza que deseja excluir esta unidade? Esta ação não pode ser desfeita.')) return;
    setMessage('');
    setError('');
    try {
      await adminService.deleteUnit(unitId); // Assumindo que deleteUnit existe no service (vou verificar/adicionar se precisar)
      setMessage('Unidade excluída com sucesso!');
      await fetchUnits();
    } catch (err) {
      setError(err.message || 'Falha ao excluir unidade');
    }
  };

  const handleStartEdit = (unit) => {
    setMessage('');
    setError('');
    setEditingUnitId(unit._id);
    setEditForm({ name: unit.name || unit.displayName || '', key: unit.key || '' });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditCancel = () => {
    setEditingUnitId(null);
    setEditForm({ name: '', key: '' });
  };

  const handleEditSave = async (unitId) => {
    setMessage('');
    setError('');
    if (!editForm.name.trim()) {
      setError('Nome da unidade é obrigatório');
      return;
    }
    if (!editForm.key.trim()) {
      setError('Chave da unidade é obrigatória');
      return;
    }
    const keyRegex = /^[a-z0-9-]{2,40}$/;
    if (!keyRegex.test(editForm.key)) {
      setError('Chave deve conter apenas letras minúsculas, números e hífen (2-40 caracteres).');
      return;
    }
    try {
      setEditSaving(true);
      await adminService.updateUnit(unitId, { name: editForm.name.trim(), key: editForm.key.trim() });
      setMessage('Unidade atualizada com sucesso!');
      setEditingUnitId(null);
      setEditForm({ name: '', key: '' });
      await fetchUnits();
    } catch (err) {
      setError(err.message || 'Falha ao atualizar unidade');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Cadastro de Unidades</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Crie novas unidades para organizar o conteúdo por local.</p>

        {error && (
          <div className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</div>
        )}
        {message && (
          <div className="mt-4 text-sm text-green-600 dark:text-green-400">{message}</div>
        )}

        <form onSubmit={handleCreate} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome da Unidade</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:border-amber-500 focus:ring-amber-500"
              placeholder="Ex.: Unidade Centro"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Chave</label>
            <input
              type="text"
              name="key"
              value={form.key}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:border-amber-500 focus:ring-amber-500"
              placeholder="Ex.: unidade-centro"
            />
            <p className="mt-1 text-xs text-gray-400">Use letras minúsculas, números e hífen.</p>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Criar Unidade'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Gerenciar Unidades</h3>
          {loadingUnits && <span className="text-sm text-gray-500">Carregando...</span>}
        </div>

        {errorUnits && (
          <div className="mt-4 text-sm text-red-600 dark:text-red-400">{errorUnits}</div>
        )}

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Chave</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {units && units.length > 0 ? (
                units.map((u) => (
                  <tr key={u._id}>
                    {editingUnitId === u._id ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            name="name"
                            value={editForm.name}
                            onChange={handleEditChange}
                            className="w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:border-amber-500 focus:ring-amber-500 text-sm"
                            placeholder="Nome da Unidade"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            name="key"
                            value={editForm.key}
                            onChange={handleEditChange}
                            className="w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:border-amber-500 focus:ring-amber-500 text-sm"
                            placeholder="Chave (slug)"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                            {u.isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditSave(u._id)}
                              disabled={editSaving}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            >
                              Salvar
                            </button>
                            <button
                              onClick={handleEditCancel}
                              disabled={editSaving}
                              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 disabled:opacity-50"
                            >
                              Cancelar
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{u.name || u.displayName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{u.key}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                            {u.isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleStartEdit(u)}
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                              title="Editar"
                            >
                              <i className="ph ph-pencil-simple text-lg"></i>
                            </button>
                            
                            <button
                              onClick={() => handleToggleStatus(u._id, u.isActive)}
                              className={`${u.isActive ? 'text-orange-600 hover:text-orange-900 dark:text-orange-400' : 'text-green-600 hover:text-green-900 dark:text-green-400'} disabled:opacity-50`}
                              title={u.isActive ? 'Desativar' : 'Ativar'}
                            >
                              <i className={`ph ${u.isActive ? 'ph-prohibit' : 'ph-check-circle'} text-lg`}></i>
                            </button>

                            <button
                              onClick={() => handleDelete(u._id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Excluir permanentemente"
                            >
                              <i className="ph ph-trash text-lg"></i>
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" colSpan={4}>
                    Nenhuma unidade encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UnitsManagement;